import { useEffect, useState } from 'react';
import { Box, MenuItem, Select, TextField } from '@mui/material';
import DefaultButton from '../../../../../components/Accessibility/DefaultButton';
import TypographyAccessibility from '../../../../../components/Accessibility/TypographyAccessibility';
import LoadingApp from '../../../../../components/LoadingApp';
import { ModalDefault } from '../../../../../components/ModalDefault';
import { TranscricaoDetalhe } from '../../../../../interfaces/entities/Transcricao/TranscricaoDetalhe';
import {
  ConselhoProfissionalItem,
  recuperarListaConselhoProfissional,
  recuperarListaUfConselhoProfissional,
  UfConselhoProfissionalItem,
} from '../../../../../services/TranscricaoBeneficiarioService';
import { sxLabel } from '../../../../../assets/styles/CommonSxProps';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import {
  sxActionsLeft,
  sxField,
  sxFormGrid,
  sxFormRow,
  sxModalTitle,
  sxSelect,
  sxTextField,
} from './editarProcedimentoModalSx';

export type CooperadoOption = 'Sim' | 'Nao' | '';

export interface EditarProfissionalFormValues {
  profissional: string;
  registro: string;
  idUfConselhoInformado: number | '';
  siglaUfConselhoInformado: string;
  codigoConselho: string;
  cooperado: CooperadoOption;
}

interface EditarProfissionalModalProps {
  open: boolean;
  detalhe: TranscricaoDetalhe | null;
  onClose: () => void;
  onSave: (values: EditarProfissionalFormValues) => void;
}

const mapCooperadoToForm = (indCooperado?: string): CooperadoOption => {
  if (!indCooperado?.trim()) return '';

  const normalized = indCooperado.trim().toUpperCase();
  if (normalized === 'S' || normalized === 'SIM') return 'Sim';
  if (normalized === 'N' || normalized === 'NAO' || normalized === 'NÃO') return 'Nao';

  return '';
};

const mapDetalheToForm = (detalhe: TranscricaoDetalhe): EditarProfissionalFormValues => ({
  profissional:
    detalhe.nomeSolicitanteInformado?.trim() ||
    detalhe.nomeSolicitante?.trim() ||
    '',
  registro: detalhe.numeroConselhoInformado ?? '',
  idUfConselhoInformado: detalhe.idUfConselhoInformado ?? '',
  siglaUfConselhoInformado: detalhe.siglaUfConselhoInformado ?? '',
  codigoConselho: detalhe.codigoConselhoInformado ?? '',
  cooperado: mapCooperadoToForm(detalhe.indCooperado),
});

const resolveUfFormValues = (
  detalhe: TranscricaoDetalhe,
  listaUf: UfConselhoProfissionalItem[],
): EditarProfissionalFormValues => {
  const formValues = mapDetalheToForm(detalhe);
  const sigla = formValues.siglaUfConselhoInformado.trim();

  if (!sigla) return formValues;

  const ufPorSigla = listaUf.find((uf) => uf.sigla === sigla);
  if (!ufPorSigla) return formValues;

  const idAtual = formValues.idUfConselhoInformado;
  const idExisteNaLista =
    idAtual !== ''
    && listaUf.some((uf) => uf.idUfConselhoInformado === idAtual);

  if (idExisteNaLista) return formValues;

  return {
    ...formValues,
    idUfConselhoInformado: ufPorSigla.idUfConselhoInformado,
    siglaUfConselhoInformado: ufPorSigla.sigla,
  };
};

const EditarProfissionalModal: React.FC<EditarProfissionalModalProps> = ({
  open,
  detalhe,
  onClose,
  onSave,
}) => {
  const accessibility = useAccessibility();
  const [carregandoListas, setCarregandoListas] = useState(false);
  const [listaUf, setListaUf] = useState<UfConselhoProfissionalItem[]>([]);
  const [listaConselho, setListaConselho] = useState<ConselhoProfissionalItem[]>([]);
  const [formValues, setFormValues] = useState<EditarProfissionalFormValues>({
    profissional: '',
    registro: '',
    idUfConselhoInformado: '',
    siglaUfConselhoInformado: '',
    codigoConselho: '',
    cooperado: '',
  });

  useEffect(() => {
    if (!open || !detalhe) return;

    let cancelled = false;

    const carregarListas = async () => {
      setCarregandoListas(true);

      try {
        const [ufs, conselhos] = await Promise.all([
          recuperarListaUfConselhoProfissional(),
          recuperarListaConselhoProfissional(),
        ]);

        if (cancelled) return;

        setListaUf(ufs ?? []);
        setListaConselho(conselhos ?? []);
        setFormValues(resolveUfFormValues(detalhe, ufs ?? []));
      } finally {
        if (!cancelled) {
          setCarregandoListas(false);
        }
      }
    };

    carregarListas();

    return () => {
      cancelled = true;
    };
  }, [open, detalhe]);

  const handleFieldChange = (
    field: 'profissional' | 'registro',
    value: string,
  ) => {
    setFormValues((current) => ({ ...current, [field]: value }));
  };

  const handleUfChange = (idUfConselhoInformado: number | '') => {
    const ufSelecionada = listaUf.find(
      (uf) => uf.idUfConselhoInformado === idUfConselhoInformado,
    );

    setFormValues((current) => ({
      ...current,
      idUfConselhoInformado,
      siglaUfConselhoInformado: ufSelecionada?.sigla ?? '',
    }));
  };

  const handleSalvar = () => {
    onSave(formValues);
    onClose();
  };

  return (
    <>
      <ModalDefault
        open={open}
        onClose={onClose}
        title="Editar"
        sxTitle={sxModalTitle}
        maxWidth={696}
      >
        <Box
          component="form"
          sx={sxFormGrid}
          style={{
            ...accessibility.textStyleAccessibility,
            ...accessibility.contrastBorderAccessibility,
          }}
          onSubmit={(event) => {
            event.preventDefault();
            handleSalvar();
          }}
        >
          <Box sx={sxField}>
            <TypographyAccessibility component="label" sx={sxLabel}>
              Profissional
            </TypographyAccessibility>
            <TextField
              value={formValues.profissional}
              onChange={(event) => handleFieldChange('profissional', event.target.value)}
              variant="outlined"
              fullWidth
              disabled={carregandoListas}
              sx={sxTextField}
              InputProps={{ style: { ...accessibility.contrastStyle } }}
            />
          </Box>

          <Box sx={sxFormRow}>
            <Box sx={sxField}>
              <TypographyAccessibility component="label" sx={sxLabel}>
                Nº Registro
              </TypographyAccessibility>
              <TextField
                value={formValues.registro}
                onChange={(event) => handleFieldChange('registro', event.target.value)}
                variant="outlined"
                fullWidth
                disabled={carregandoListas}
                sx={sxTextField}
                InputProps={{ style: { ...accessibility.contrastStyle } }}
              />
            </Box>
            <Box sx={sxField}>
              <TypographyAccessibility component="label" sx={sxLabel}>
                UF
              </TypographyAccessibility>
              <Select
                value={formValues.idUfConselhoInformado}
                onChange={(event) => {
                  const value = event.target.value;
                  handleUfChange(value === '' ? '' : Number(value));
                }}
                displayEmpty
                fullWidth
                disabled={carregandoListas}
                sx={sxSelect}
                style={accessibility.contrastStyle}
              >
                <MenuItem value="">
                  Selecione
                </MenuItem>
                {listaUf.map((uf) => (
                  <MenuItem key={uf.idUfConselhoInformado} value={uf.idUfConselhoInformado}>
                    {uf.sigla}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box sx={sxFormRow}>
            <Box sx={sxField}>
              <TypographyAccessibility component="label" sx={sxLabel}>
                Tipo
              </TypographyAccessibility>
              <Select
                value={formValues.codigoConselho}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    codigoConselho: event.target.value,
                  }))
                }
                displayEmpty
                fullWidth
                disabled={carregandoListas}
                sx={sxSelect}
                style={accessibility.contrastStyle}
              >
                <MenuItem value="">
                  Selecione
                </MenuItem>
                {listaConselho.map((conselho) => (
                  <MenuItem key={conselho.codigo} value={conselho.codigo}>
                    {conselho.codigo}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box sx={sxActionsLeft}>
            <DefaultButton variant="outlined" text="VOLTAR" onClick={onClose} />
            <DefaultButton text="SALVAR" type="submit" disabled={carregandoListas} />
          </Box>
        </Box>
      </ModalDefault>

      <LoadingApp showLoading={carregandoListas} text="Carregando..." />
    </>
  );
};

export default EditarProfissionalModal;
